import React, { useState, useEffect, useCallback } from "react";
import {
    getMySubscription,
    getSubscriptionUploadUrl,
    submitSubscriptionPayment,
} from "../../services/SubscriptionService";
import { formatINR, formatDate, downloadInvoicePdf, formatBillingCycle } from "./subscriptionUtils";

/**
 * SubscriptionGate renders nothing for healthy/non-applicable users.
 * - role 2 (master & branch users) with an expiring/grace subscription → premium reminder banner
 * - role 2 with an expired subscription → full-screen blocking pay-wall
 * Fails open: any error or missing config never blocks the portal.
 */
const SubscriptionGate = () => {
    const [data, setData] = useState(null);
    const [loaded, setLoaded] = useState(false);
    const [bannerDismissed, setBannerDismissed] = useState(false);
    const [payOpen, setPayOpen] = useState(false);

    const userData = (() => {
        try { return JSON.parse(localStorage.getItem("userData")); } catch { return null; }
    })();

    const applies = userData && userData.role === 2 && userData.masterId;

    const load = useCallback(async () => {
        try {
            const res = await getMySubscription();
            if (res && res.success) setData(res.data);
        } catch (e) {
            // fail open
        } finally {
            setLoaded(true);
        }
    }, []);

    useEffect(() => {
        if (applies) load();
        else setLoaded(true);
    }, [applies, load]);

    if (!applies || !loaded || !data) return null;

    const state = data?.status?.state;
    const hasPending = !!data?.pendingPayment;

    // Nothing to show for healthy or unconfigured subscriptions
    if (!state || state === "none" || state === "active") return null;

    const isExpired = state === "expired";

    const onPaid = () => { setPayOpen(false); load(); };

    return (
        <>
            {isExpired && <BodyScrollLock />}
            {state === "expired" ? (
                <ExpiredScreen data={data} onPay={() => setPayOpen(true)} hasPending={hasPending} />
            ) : (
                !bannerDismissed && (
                    <ReminderBanner
                        data={data}
                        state={state}
                        hasPending={hasPending}
                        onPay={() => setPayOpen(true)}
                        onDismiss={() => setBannerDismissed(true)}
                    />
                )
            )}

            {payOpen && (
                <PaymentModal data={data} onClose={() => setPayOpen(false)} onPaid={onPaid} />
            )}
        </>
    );
};

/* ---------------------------- Reminder banner ---------------------------- */
const ReminderBanner = ({ data, state, hasPending, onPay, onDismiss }) => {
    const days = data?.status?.daysRemaining;
    const overdue = data?.status?.daysOverdue;
    const isGrace = state === "grace";
    return (
        <div className="fixed bottom-4 right-4 z-[9990] w-[92vw] max-w-sm">
            <div className={`rounded-2xl shadow-2xl border overflow-hidden ${isGrace ? "border-orange-200" : "border-amber-200"} bg-white`}>
                <div className={`px-4 py-3 flex items-center gap-3 ${isGrace ? "bg-gradient-to-r from-orange-500 to-rose-500" : "bg-gradient-to-r from-amber-400 to-amber-500"}`}>
                    <div className="h-9 w-9 rounded-full bg-white/20 flex items-center justify-center text-white text-lg">⏳</div>
                    <div className="text-white">
                        <p className="font-semibold leading-tight">
                            {isGrace ? "Subscription Expired" : "Subscription Expiring Soon"}
                        </p>
                        <p className="text-xs opacity-90">
                            {isGrace
                                ? `Expired ${overdue} day${overdue === 1 ? "" : "s"} ago · grace period active`
                                : `Renews in ${days} day${days === 1 ? "" : "s"}`}
                        </p>
                    </div>
                    <button onClick={onDismiss} className="ml-auto text-white/80 hover:text-white text-xl leading-none">×</button>
                </div>
                <div className="p-4">
                    <div className="flex justify-between text-sm mb-3">
                        <span className="text-gray-500">{data.subscription?.planName}</span>
                        <span className="font-bold text-gray-900">{formatINR(data.subscription?.totalAmount)}</span>
                    </div>
                    {hasPending ? (
                        <div className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2">
                            Payment received — verification in progress. Your portal stays active.
                        </div>
                    ) : (
                        <button onClick={onPay} className="w-full py-2.5 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold hover:opacity-95">
                            Renew Now
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

/* ---------------------------- Expired pay-wall ---------------------------- */
const ExpiredScreen = ({ data, onPay, hasPending }) => {
    const company = data?.settings?.company || {};
    const sub = data?.subscription;
    const paidInvoices = (data?.invoices || []).filter((i) => i.status === "paid");
    const support = data?.settings || {};

    return (
        <div className="fixed inset-0 z-[99990] overflow-y-auto bg-gradient-to-br from-slate-900 via-slate-800 to-indigo-950">
            <div className="min-h-full flex items-center justify-center p-4 py-10">
                <div className="w-full max-w-4xl">
                    {/* Brand header */}
                    <div className="text-center mb-8">
                        {company.logoUrl ? (
                            <img src={company.logoUrl} alt={company.name} className="h-14 mx-auto mb-4 object-contain" />
                        ) : (
                            <div className="inline-flex items-center justify-center h-14 w-14 rounded-2xl bg-white/10 text-white text-2xl font-bold mb-4">
                                {(company.name || "Z").charAt(0)}
                            </div>
                        )}
                        <h1 className="text-3xl sm:text-4xl font-bold text-white">{company.name || "Your Service Provider"}</h1>
                        <p className="text-slate-300 mt-2">Your subscription has expired</p>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                        {/* Left: details */}
                        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-6">
                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-rose-100 text-rose-700 text-xs font-semibold mb-4">
                                <span className="h-1.5 w-1.5 rounded-full bg-rose-500 animate-pulse"></span>
                                ACCESS SUSPENDED
                            </div>
                            <h2 className="text-xl font-bold text-gray-900 mb-1">Renew to restore access</h2>
                            <p className="text-sm text-gray-500 mb-5">
                                To keep your store, orders and operations running, please renew your subscription.
                            </p>

                            <div className="rounded-xl border border-gray-200 divide-y divide-gray-100">
                                <Row label="Plan" value={sub?.planName} />
                                <Row label="Billing cycle" value={formatBillingCycle(sub?.billingCycle)} />
                                <Row label="Expired on" value={formatDate(sub?.endDate)} />
                                {sub?.gstEnabled && <Row label={`GST (${sub.gstRate}%)`} value={formatINR(sub.gstAmount)} />}
                                <div className="flex justify-between items-center px-4 py-3">
                                    <span className="text-gray-500 text-sm">Amount due</span>
                                    <span className="text-2xl font-bold text-gray-900">{formatINR(sub?.totalAmount)}</span>
                                </div>
                            </div>

                            {(sub?.features || []).filter((f) => f.enabled).length > 0 && (
                                <div className="mt-4">
                                    <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Included features</p>
                                    <div className="flex flex-wrap gap-1.5">
                                        {sub.features.filter((f) => f.enabled).map((f, i) => (
                                            <span key={i} className="px-2.5 py-1 rounded-full bg-gray-100 text-gray-700 text-xs">
                                                {f.name} · {formatINR(f.price)}
                                            </span>
                                        ))}
                                    </div>
                                </div>
                            )}

                            {paidInvoices.length > 0 && (
                                <div className="mt-5">
                                    <p className="text-xs font-semibold text-gray-400 uppercase mb-2">Your invoices</p>
                                    <div className="space-y-1.5">
                                        {paidInvoices.slice(0, 4).map((inv) => (
                                            <button key={inv._id} onClick={() => downloadInvoicePdf(inv)}
                                                className="w-full flex justify-between items-center text-sm px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50">
                                                <span className="text-gray-700">{inv.invoiceNumber}</span>
                                                <span className="text-blue-600 font-medium">Download PDF</span>
                                            </button>
                                        ))}
                                    </div>
                                </div>
                            )}
                        </div>

                        {/* Right: action / status */}
                        <div className="bg-white/95 backdrop-blur rounded-2xl shadow-2xl p-6 flex flex-col">
                            {hasPending ? (
                                <div className="flex-1 flex flex-col items-center justify-center text-center py-6">
                                    <div className="h-16 w-16 rounded-full bg-blue-100 flex items-center justify-center text-3xl mb-4">🕐</div>
                                    <h3 className="text-lg font-bold text-gray-900">Payment under verification</h3>
                                    <p className="text-sm text-gray-500 mt-2 max-w-xs">
                                        We have received your payment proof. Our team will verify it against our account and
                                        activate your portal {support.activationSlaText || "within 1 hour"}.
                                    </p>
                                    <p className="text-xs text-gray-400 mt-4">Submitted {formatDate(data.pendingPayment?.metaData?.createdAt)}</p>
                                </div>
                            ) : (
                                <>
                                    <h3 className="text-lg font-bold text-gray-900 mb-1">Pay & upload proof</h3>
                                    <p className="text-sm text-gray-500 mb-4">Scan the QR with any UPI app, then submit your payment screenshot.</p>
                                    <div className="flex flex-col items-center bg-gray-50 rounded-xl p-5 mb-4">
                                        {data.settings?.qrImageUrl ? (
                                            <div className="bg-white p-3 rounded-xl shadow-sm">
                                                <img src={data.settings.qrImageUrl} alt="Payment QR" className="h-44 w-44 object-contain" />
                                            </div>
                                        ) : (
                                            <div className="text-sm text-gray-400 text-center py-6">QR not configured.<br />Use UPI ID / bank details below.</div>
                                        )}
                                        {data.settings?.upiId && (
                                            <p className="mt-3 text-sm font-medium text-gray-700">{data.settings.upiId}</p>
                                        )}
                                        <p className="text-2xl font-bold text-gray-900 mt-1">{formatINR(sub?.totalAmount)}</p>
                                    </div>
                                    <button onClick={onPay} className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold hover:opacity-95">
                                        I've Paid — Upload Screenshot
                                    </button>
                                </>
                            )}

                            {/* Support / company footer */}
                            <div className="mt-6 pt-4 border-t border-gray-100 text-xs text-gray-500 space-y-1">
                                {[company.addressLine1, company.addressLine2, company.city, company.state, company.pincode].filter(Boolean).length > 0 && (
                                    <p>{[company.addressLine1, company.addressLine2, company.city, company.state, company.pincode].filter(Boolean).join(", ")}</p>
                                )}
                                {company.gstin && <p>GSTIN: {company.gstin}</p>}
                                {(support.supportEmail || support.supportPhone) && (
                                    <p>Support: {[support.supportEmail, support.supportPhone].filter(Boolean).join(" · ")}</p>
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

/* ---------------------------- Payment modal ---------------------------- */
const PaymentModal = ({ data, onClose, onPaid }) => {
    const sub = data?.subscription;
    const company = data?.settings?.company || {};
    const bank = data?.settings?.bankDetails || {};
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState("");
    const [payerName, setPayerName] = useState("");
    const [reference, setReference] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");
    const [done, setDone] = useState(false);

    const onSelectFile = (e) => {
        const f = e.target.files?.[0];
        if (!f) return;
        setFile(f);
        setPreview(URL.createObjectURL(f));
        setError("");
    };

    const handleSubmit = async () => {
        if (!file) { setError("Please attach your payment screenshot."); return; }
        setSubmitting(true);
        setError("");
        try {
            const urlRes = await getSubscriptionUploadUrl(file.name, file.type || "image/png");
            if (!urlRes.success || !urlRes.data?.uploadUrl) throw new Error("Could not prepare upload");
            const putRes = await fetch(urlRes.data.uploadUrl, {
                method: "PUT",
                headers: { "Content-Type": file.type || "image/png" },
                body: file,
            });
            if (!putRes.ok) throw new Error("Upload failed, please retry");

            const res = await submitSubscriptionPayment({
                amount: sub?.totalAmount,
                screenshotUrl: urlRes.data.publicUrl,
                screenshotFileName: file.name,
                payerName,
                referenceNote: reference,
                upiId: data?.settings?.upiId || "",
            });
            if (!res.success) throw new Error(res.error || "Submission failed");
            setDone(true);
        } catch (e) {
            setError(e.message || "Something went wrong");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div className="fixed inset-0 z-[100000] flex items-center justify-center bg-black/70 p-4" onClick={() => !submitting && onClose()}>
            <div className="bg-white rounded-2xl w-full max-w-lg max-h-[92vh] overflow-y-auto" onClick={(e) => e.stopPropagation()}>
                <div className="flex justify-between items-center px-6 py-4 border-b border-gray-200">
                    <h3 className="text-lg font-bold text-gray-900">{done ? "Submitted" : "Complete Payment"}</h3>
                    <button onClick={() => !submitting && onClose()} className="text-gray-400 hover:text-gray-700 text-2xl">×</button>
                </div>

                {done ? (
                    <div className="p-8 text-center">
                        <div className="h-16 w-16 rounded-full bg-emerald-100 flex items-center justify-center text-3xl mx-auto mb-4">✓</div>
                        <h4 className="text-xl font-bold text-gray-900">Thank you!</h4>
                        <p className="text-gray-500 mt-2">
                            We'll verify your payment with our account and activate your portal {data?.settings?.activationSlaText || "within 1 hour"}.
                        </p>
                        <button onClick={onPaid} className="mt-6 px-6 py-2.5 rounded-lg bg-blue-600 text-white hover:bg-blue-700">Done</button>
                    </div>
                ) : (
                    <div className="p-6 space-y-5">
                        {/* QR + amount */}
                        <div className="flex flex-col items-center bg-gray-50 rounded-xl p-5">
                            {data?.settings?.qrImageUrl ? (
                                <div className="bg-white p-3 rounded-xl shadow-sm">
                                    <img src={data.settings.qrImageUrl} alt="Payment QR" className="h-40 w-40 object-contain" />
                                </div>
                            ) : (
                                <p className="text-sm text-gray-400">QR not configured — use UPI ID / bank details</p>
                            )}
                            {data?.settings?.upiId && <p className="mt-3 text-sm font-medium text-gray-700">{data.settings.upiId}</p>}
                            <p className="text-2xl font-bold text-gray-900 mt-1">{formatINR(sub?.totalAmount)}</p>
                            {bank.accountNumber && (
                                <p className="text-xs text-gray-500 mt-2 text-center">
                                    {bank.bankName} · A/C {bank.accountNumber} · IFSC {bank.ifsc}
                                </p>
                            )}
                        </div>

                        <div className="space-y-3">
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Payment Screenshot *</label>
                                <input type="file" accept="image/*" onChange={onSelectFile} className="block w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100" />
                                {preview && <img src={preview} alt="preview" className="mt-3 max-h-48 rounded-lg border border-gray-200" />}
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Payer Name</label>
                                <input className="zn-input" value={payerName} onChange={(e) => setPayerName(e.target.value)} placeholder="Name as in payment" />
                            </div>
                            <div>
                                <label className="block text-sm font-medium text-gray-600 mb-1">Reference / UTR (optional)</label>
                                <input className="zn-input" value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Transaction reference" />
                            </div>
                        </div>

                        {error && <p className="text-sm text-rose-600 bg-rose-50 border border-rose-200 rounded-lg px-3 py-2">{error}</p>}

                        <button onClick={handleSubmit} disabled={submitting} className="w-full py-3 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 text-white font-semibold hover:opacity-95 disabled:opacity-60">
                            {submitting ? "Submitting..." : "Submit for Verification"}
                        </button>
                        <p className="text-xs text-gray-400 text-center">
                            After submission, we'll validate and activate your portal {data?.settings?.activationSlaText || "within 1 hour"}.
                        </p>
                    </div>
                )}
            </div>
        </div>
    );
};

// Locks background scrolling while the expired pay-wall is shown.
const BodyScrollLock = () => {
    useEffect(() => {
        const prev = document.body.style.overflow;
        document.body.style.overflow = "hidden";
        return () => { document.body.style.overflow = prev; };
    }, []);
    return null;
};

const Row = ({ label, value, capitalize }) => (
    <div className="flex justify-between items-center px-4 py-2.5">
        <span className="text-gray-500 text-sm">{label}</span>
        <span className={`text-gray-900 font-medium text-sm ${capitalize ? "capitalize" : ""}`}>{value || "—"}</span>
    </div>
);

export default SubscriptionGate;
